<template>
  <div style="display: flex; flex-direction: column; gap: .5rem;">
    <div style="display: flex; gap: .5rem;">
      <InputText v-model="query" style="flex: 1;" :placeholder="$t('Search...')"/>
      <Button @click="query=''; getFiles();" severity="secondary" icon="fa fa-times"/>
      <Button @click="getFiles" icon="fa fa-search"/>
    </div>
    <DataTable @update:sortField="updateSortField" @update:sortOrder="updateSortOrder" :sortField="sortField" :sortOrder="sortOrder" :value="files" stripedRows responsiveLayout="scroll">
      <Column sortable key="fileName" field="fileName" :header="$t('File name')" style="min-width: 7.5rem;">
        <template #body="slotProps">
          {{ slotProps.data.fileName }}
        </template>
      </Column>
      <Column sortable key="creationTime" field="creationTime" :header="$t('Created at')" style="min-width: 10rem;">
        <template #body="slotProps">
          {{ slotProps.data.creationTime }}
        </template>
      </Column>
      <Column sortable key="modifiedTime" field="modifiedTime" :header="$t('Modified at')" style="min-width: 10rem;">
        <template #body="slotProps">
          {{ slotProps.data.modifiedTime }}
        </template>
      </Column>
      <Column sortable key="size" field="size" :header="$t('Size')" style="min-width: 5rem;">
        <template #body="slotProps">
          {{ slotProps.data.size }}
        </template>
      </Column>
      <Column key="actions">
        <template #body="slotProps">
          <Button
            :severity="selectedFiles.includes(slotProps.data.fileName) ? 'primary' : 'secondary'"
            :style="!selectedFiles.includes(slotProps.data.fileName) ? 'border: 1px solid var(--surface-500);' : ''"
            :icon="`fa ${selectedFiles.includes(slotProps.data.fileName) ? 'fa-check' : ''}`"
            @click="selectedFiles.includes(slotProps.data.fileName) ? selectedFiles.splice(selectedFiles.findIndex(sf => sf === slotProps.data.fileName), 1) : selectedFiles.push(slotProps.data.fileName)"
          />
        </template>
      </Column>
    </DataTable>
    <Paginator
      :first="skip"
      :rows="take"
      :totalRecords="totalRecords"
      template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
      @page="onPageChange($event)"
      v-if="totalRecords > take"
    />
    <template v-if="selectedFiles.length">
      <div style="display: flex; gap: .5rem; align-items: center; border: 1px solid var(--surface-50); padding: .5rem; border-radius: var(--border-radius);">
        <strong>{{ $t('selected files') }}:</strong>
        <div v-for="sf in selectedFiles" style="display: flex; gap: .5rem; align-items: center; padding: .25rem .5rem; background: var(--surface-50); border-radius: var(--border-radius);">
          {{ sf }}
          <icon class="fa fa-times" style="cursor: pointer;" @click="selectedFiles.splice(selectedFiles.findIndex(sfAux => sfAux === sf), 1)"/>
        </div>
      </div>
    </template>
    <hr style="margin: 0;">
    <div style="display: flex; gap: .5rem; align-items: center; justify-content: right;">
      <Button @click="$emit('closeDialog')" severity="secondary" :label="$t('Cancel')" icon="fa fa-times"/>
      <Button @click="uploadFiles" :disabled="!selectedFiles.length" :label="$t('Upload files')" icon="fa fa-upload"/>
    </div>
  </div>
  <Loader :is-loading="isLoading" :is-local-loader="true" />
</template>

<script>
import Loader from '@/components/common/Loader.vue';
import prettyBytes from 'pretty-bytes';
import {SignalRHub} from '@/services/signalr-hub';
import { b64toBlob } from '@/js/utils';

export default {
  name: 'GalleryUpload',
  components: {
    Loader
  },
  data() {
    return {
      skip: 0,
      take: 10,
      totalRecords: 0,
      isLoading: false,
      selectedFiles: [],
      files: [],
      selectedFullFiles: [],
      hub: null,
      tempFiles: [],
      query: '',
      sortField: 'fileName',
      sortOrder: 1,
      searchTimeout: null
    }
  },
  async created() {
    await this.getFiles();
  },
  mounted() {
    this.hub = new SignalRHub();
    this.hub.on('fileCreated', this.onFileCreated);
    this.hub.on('fileChunk', this.onFileChunk);
    this.hub.on('fileCompleted', this.onFileCompleted);
    this.hub.start();
  },
  methods: {
    updateSortField(ev) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.sortField = ev;
      this.searchTimeout = setTimeout(() => {
        this.getFiles();
      }, 1);
    },
    updateSortOrder(ev) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.sortOrder = ev;
      this.searchTimeout = setTimeout(() => {
        this.getFiles();
      }, 1);
    },
    onFileCreated(data) {
      const { fileName, size, totalChunks } = data;
      if (!fileName || !totalChunks) return;

      this.isLoading = true;

      this.tempFiles.push({
        fileName,
        size: Number(size || 0),
        totalChunks: Number(totalChunks),
        receivedChunks: 0,
        receivedBytes: 0,
        parts: [],
        state: 'receiving',
        uploading: false,
        done: false,
      });
    },
    onFileChunk(data) {
      const { fileName, chunkIndex, chunk, totalChunks } = data || {};
      const fileExists = this.tempFiles.find((x) => x.fileName === fileName);
      if (!fileExists) return;

      try {
        const bytes = b64toBlob(String(chunk));
        fileExists.parts[chunkIndex] = bytes;
        fileExists.receivedChunks = Math.max(fileExists.receivedChunks, chunkIndex + 1);
        fileExists.receivedBytes += bytes.byteLength;
        fileExists.totalChunks = Number(totalChunks || fileExists.totalChunks);
      } catch (error) {
        fileExists.state = 'error';
        fileExists.error = error?.message || 'decode error';
      }
    },
    onFileCompleted(data) {
      const fileExists = this.tempFiles.find((x) => x.fileName === data.fileName);
      if (!fileExists) return;

      try {
        fileExists.state = 'assembling';
        const merged = new Blob(fileExists.parts, { type: 'application/pdf' });
        fileExists.blob = merged;
        fileExists.file = new File([merged], fileExists.fileName, { type: 'application/pdf' });
        fileExists.state = 'ready';

        this.selectedFullFiles.push(fileExists)
        this.tempFiles.splice(this.tempFiles.indexOf(fileExists), 1);

        // Set isLoading to false when all files are completed
        if (this.tempFiles.length === 0) {
          this.isLoading = false;
        }
      } catch (error) {
        fileExists.state = 'error';
        fileExists.error = error?.message || 'assemble error';
      }
    },
    async uploadFiles() {
      this.isLoading = true;
      try {
        await this.$store.dispatch('postGalleryFiles', { files: this.selectedFiles });
        this.$emit('filesSelected', this.selectedFullFiles);
        this.$emit('closeDialog');
      } catch (err) {
        console.log(err);
      }
      this.isLoading = false;
    },
    async getFiles() {
      this.isLoading = true;
      try {
        const queryData = {
          page: (this.skip/this.take)+1,
          pageSize: this.take
        };
        if (this.sortField && this.sortOrder) {
          queryData.orderBy = `${this.sortField}:${this.sortOrder > 0 ? 'asc' : 'desc'}`;
        }
        if (this.query) {
          queryData.search = this.query;
        }
        const data = await this.$store.dispatch('getGalleryFiles', queryData);
        this.files = data.items.map(it => ({
          fileName: it.fileName,
          creationTime: this.$moment(it.creationTime).format('DD/MM/YYYY | HH:mm'),
          modifiedTime: this.$moment(it.modifiedTime).format('DD/MM/YYYY | HH:mm'),
          size: prettyBytes(it.size)
        }));
        this.totalRecords = data.totalItems;
      } catch (err) {
        console.log(err);
      }
      this.isLoading = false;
    },
    onPageChange(ev) {
      this.skip = ev.page * ev.rows;
      this.take = ev.rows;
      this.getFiles();
    }
  },
  beforeUnmount() {
    if (this.hub) {
      this.hub.off('fileCreated', this.onFileCreated);
      this.hub.off('fileChunk', this.onFileChunk);
      this.hub.off('fileCompleted', this.onFileCompleted);
      this.hub.stop();
    }
  }
}
</script>
